<app-modal [isOpen]="isOpen" (close)="onClose()" className="max-w-2xl mx-4 sm:mx-6">
  @if (form) {
  <div class="p-6 sm:p-8">

    <!-- Header -->
    <div class="flex items-start gap-5 mb-8">
      <!-- Avatar -->
      <div class="flex-shrink-0">
        <div class="flex h-16 w-16 items-center justify-center rounded-full text-xl font-bold {{ avatarColorClass }}">
          {{ initials }}
        </div>
      </div>

      <!-- Name + badges + notas internas -->
      <div class="flex-1 min-w-0">
        @if (editingName) {
          <input
            #nameInput
            type="text"
            [(ngModel)]="editNameValue"
            (blur)="saveName()"
            (keydown)="onNameKeydown($event)"
            [disabled]="savingName"
            class="w-full text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-brand-500
                   outline-none mb-2 leading-tight disabled:opacity-60"
          />
        } @else {
          <h2
            (click)="startEditName()"
            class="text-2xl font-bold text-gray-900 dark:text-white mb-2 cursor-pointer
                   hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            title="Haz clic para editar el nombre">
            {{ form.name }}
          </h2>
        }
        <div class="flex items-center gap-2 flex-wrap">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {{ typeColorClass }}">
            {{ form.formType | formTypeLabel }}
          </span>
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {{ statusColor }}">
            {{ form.status | formStatus }}
          </span>
          @if (form.isTemplate) {
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400">
              Plantilla
            </span>
          }
        </div>

        <!-- Nota interna editable -->
        <div class="mt-3">
          @if (editingNotes) {
            <div class="flex flex-col gap-2">
              <textarea
                [(ngModel)]="editNotesValue"
                rows="3"
                placeholder="Añade una nota interna..."
                class="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none">
              </textarea>
              <div class="flex items-center gap-2">
                <button (click)="saveNotes()" [disabled]="savingNotes"
                  class="px-3 py-1 text-xs font-medium rounded-lg bg-brand-500 hover:bg-brand-600 text-white transition-colors disabled:opacity-50">
                  {{ savingNotes ? 'Guardando...' : 'Guardar' }}
                </button>
                <button (click)="cancelEditNotes()" [disabled]="savingNotes"
                  class="px-3 py-1 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  Cancelar
                </button>
              </div>
            </div>
          } @else {
            <button (click)="startEditNotes()"
              class="group flex items-start gap-2 text-left w-full rounded-lg p-1.5 -ml-1.5
                     hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
              @if (formInternalNotes) {
                <span class="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">{{ formInternalNotes }}</span>
              } @else {
                <span class="text-sm text-gray-400 dark:text-gray-500 italic">Añadir nota interna...</span>
              }
              <svg class="w-3.5 h-3.5 ml-auto flex-shrink-0 mt-0.5 text-gray-300 dark:text-gray-600
                          opacity-0 group-hover:opacity-100 transition-opacity"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </button>
          }
        </div>
      </div>

      <!-- Edit button -->
      <div class="flex-shrink-0">
        <button (click)="onEdit()"
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-brand-500 hover:bg-brand-600 text-white transition-colors">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
          </svg>
          Editar
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
      <nav class="-mb-px flex gap-1">
        <button (click)="onTabChange('info')"
          class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
          [class.border-brand-500]="activeTab === 'info'"
          [class.text-brand-600]="activeTab === 'info'"
          [class.dark:text-brand-400]="activeTab === 'info'"
          [class.border-transparent]="activeTab !== 'info'"
          [class.text-gray-500]="activeTab !== 'info'"
          [class.dark:text-gray-400]="activeTab !== 'info'">
          Información
        </button>
        <button (click)="onTabChange('timeline')"
          class="whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
          [class.border-brand-500]="activeTab === 'timeline'"
          [class.text-brand-600]="activeTab === 'timeline'"
          [class.dark:text-brand-400]="activeTab === 'timeline'"
          [class.border-transparent]="activeTab !== 'timeline'"
          [class.text-gray-500]="activeTab !== 'timeline'"
          [class.dark:text-gray-400]="activeTab !== 'timeline'">
          Línea de Tiempo
        </button>
      </nav>
    </div>

    <!-- TAB: INFO -->
    @if (activeTab === 'info') {
    <div class="space-y-6">

      <!-- Información del formulario -->
      <div>
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
          Información del Formulario
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">

          <!-- Nombre -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Nombre</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white break-words">{{ form.name }}</p>
            </div>
          </div>

          <!-- Tipo -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Tipo</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ form.formType | formTypeLabel }}</p>
            </div>
          </div>

          <!-- Estado -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Estado</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ form.status | formStatus }}</p>
            </div>
          </div>

          <!-- Slug -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">URL del Formulario</p>
              <div class="flex items-center gap-1.5 mt-0.5">
                <a [href]="getPublicUrl()" target="_blank" rel="noopener"
                  class="text-sm font-mono text-brand-600 dark:text-brand-400 hover:underline break-all min-w-0">
                  {{ getPublicUrl() }}
                </a>
                <button type="button" (click)="copyPublicUrl()" title="Copiar enlace"
                  class="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-colors">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

        </div>

        <!-- Descripción pública (solo si existe) -->
        @if (formDescription) {
          <div class="mt-5 flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h10"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Descripción del formulario</p>
              <p class="text-sm text-gray-700 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{{ formDescription }}</p>
            </div>
          </div>
        }

      </div>

      <!-- Timestamps + creado por -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">

          <!-- Fecha de creación -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Fecha de Creación</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(form.datetimeCreated) }}</p>
            </div>
          </div>

          <!-- Creado por -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Creado por</p>
              @if (loadingUser) {
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500 mt-1"></div>
              } @else if (createdByUser) {
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ createdByUser.fullname }} {{ createdByUser.lastname || '' }}
                </p>
              } @else {
                <p class="text-sm text-gray-400 dark:text-gray-500">Sin información</p>
              }
            </div>
          </div>

          <!-- Última actualización -->
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs text-gray-500 dark:text-gray-400">Última Actualización</p>
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ formatDate(form.datetimeUpdated) }}</p>
            </div>
          </div>

        </div>
      </div>

      <!-- Notas internas múltiples (inc29) -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Notas internas
        </h3>

        <!-- Agregar nota -->
        <div class="flex flex-col gap-2 mb-4">
          <textarea
            [(ngModel)]="newNote"
            rows="2"
            placeholder="Escribe una nota interna para tu equipo..."
            class="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600
                   bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                   focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none">
          </textarea>
          <div class="flex justify-end">
            <button (click)="addNote()" [disabled]="!newNote.trim() || savingNote"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 hover:bg-brand-600 text-white transition-colors disabled:opacity-50">
              @if (savingNote) {
                <span class="animate-spin inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full"></span>
              }
              Agregar nota
            </button>
          </div>
        </div>

        <!-- Lista de notas -->
        @if (loadingNotes) {
          <p class="text-sm text-gray-400 dark:text-gray-500">Cargando notas…</p>
        } @else if (notes.length === 0) {
          <p class="text-sm text-gray-400 dark:text-gray-500 italic">Aún no hay notas internas.</p>
        } @else {
          <div class="space-y-2">
            @for (note of notes; track note.id) {
              <div class="group flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div class="min-w-0 flex-1">
                  <p class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{{ note.note }}</p>
                  <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{{ formatDate(note.datetimeCreated) }}</p>
                </div>
                <button (click)="removeNote(note)" title="Eliminar nota"
                  class="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-500/10 transition-colors opacity-0 group-hover:opacity-100">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            }
          </div>
        }
      </div>

    </div>
    }

    <!-- TAB: TIMELINE -->
    @if (activeTab === 'timeline') {
    <div>
      <app-form-audit-timeline
        [logs]="timelineLogs"
        [loading]="loadingTimeline"
        [error]="timelineError"
        [currentPage]="timelinePage"
        [totalPages]="timelineTotalPages"
        [totalElements]="timelineTotalElements"
        (pageChange)="loadTimeline($event)" />
    </div>
    }

  </div>
  }
</app-modal>
